/**
 * Provenance. SPEC §3.2.
 *
 * Every fact the system displays and every rule it applies carries its source,
 * and the source renders on screen. There are five values and they are
 * visually distinct.
 */

export const PROVENANCE_KINDS = [
  /** Cites the section of the Act or the Rules. */
  "statute",
  /** Cites a named KSIDC question and answer. */
  "ksidc",
  /** Cites a public register and the month it was gathered. */
  "register",
  /** A stand-in, marked at the point a reader would otherwise take it for real. */
  "generated",
  /** Our reading, not yet confirmed. Appears on the assumption register screen. */
  "assumed",
] as const;

export type ProvenanceKind = (typeof PROVENANCE_KINDS)[number];

export interface Provenance {
  kind: ProvenanceKind;
  /** e.g. "s.7", "r.9(2)", "Reply sheet Q7", "K-SWIFT clearance catalogue, Aug 2026" */
  ref: string;
  /** Optional one line of explanation, shown next to the label. */
  note?: string;
}

export const statute = (ref: string, note?: string): Provenance =>
  note === undefined ? { kind: "statute", ref } : { kind: "statute", ref, note };

export const ksidc = (ref: string, note?: string): Provenance =>
  note === undefined ? { kind: "ksidc", ref } : { kind: "ksidc", ref, note };

export const register = (ref: string, note?: string): Provenance =>
  note === undefined ? { kind: "register", ref } : { kind: "register", ref, note };

export const generated = (ref: string, note?: string): Provenance =>
  note === undefined ? { kind: "generated", ref } : { kind: "generated", ref, note };

export const assumed = (ref: string, note?: string): Provenance =>
  note === undefined ? { kind: "assumed", ref } : { kind: "assumed", ref, note };

/** True where the value must render as an assumption and appear on the register screen. */
export const isOpenAssumption = (p: Provenance): boolean => p.kind === "assumed";

/** True where the reader would otherwise take a stand-in for a real record. */
export const mustLabelAsStandIn = (p: Provenance): boolean => p.kind === "generated";

/** A value carrying where it came from. */
export interface Sourced<T> {
  value: T;
  provenance: Provenance;
}

export const sourced = <T>(value: T, provenance: Provenance): Sourced<T> => ({
  value,
  provenance,
});
